import { useLocation, useNavigate, Link } from "react-router-dom";
import { Search, Sun, Moon, LogOut, Settings, ChevronRight } from "lucide-react";
import { NotificationsBell } from "@/components/layout/NotificationsBell";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";

const ROUTE_LABELS: Record<string, string> = {
  home: "Dashboard",
  ambiental: "Ambiental",
  social: "Social",
  sst: "SST",
  legal: "Legal",
  "gestor-gdb": "GDB",
  "reporte-integrado": "Reporte ICA",
  "reporte-ambiental": "Reporte Ambiental",
  "reporte-sst": "Reporte SST",
  "reporte-social": "Reporte Social",
  "documentos-sociales": "Documentos Sociales",
  "gestor-ambiental": "Gestor Ambiental",
  "gestor-social": "Gestor Social",
  "gestor-sst": "Gestor SST",
  herramientas: "Herramientas",
  configuraciones: "Configuración",
  "gestion-usuarios": "Usuarios",
  "configuracion-roles": "Roles",
  "configuracion-base-datos": "Base de Datos",
  "configuracion-notificaciones": "Notificaciones",
  "manual-marca": "Manual de Marca",
};

export function AppTopbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { signOut, user } = useAuth();

  const segments = pathname.split("/").filter(Boolean);
  const crumbs = segments.map((seg, i) => ({
    label: ROUTE_LABELS[seg] ?? seg,
    href: "/" + segments.slice(0, i + 1).join("/"),
  }));

  const initials =
    user?.email?.split("@")[0]?.slice(0, 2).toUpperCase() ?? "V";

  return (
    <header className="sticky top-0 z-40 h-16 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="h-full px-3 sm:px-6 flex items-center gap-3">
        <SidebarTrigger className="h-9 w-9 -ml-1" />

        {/* Breadcrumbs */}
        <nav className="hidden md:flex items-center gap-1.5 text-sm min-w-0">
          <Link
            to="/home"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Viabiliza
          </Link>
          {crumbs.map((c, i) => (
            <div key={c.href} className="flex items-center gap-1.5 min-w-0">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
              <Link
                to={c.href}
                className={`truncate ${
                  i === crumbs.length - 1
                    ? "font-medium text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {c.label}
              </Link>
            </div>
          ))}
        </nav>

        {/* Search */}
        <div className="flex-1 flex justify-end md:justify-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar obligaciones, documentos, usuarios…"
              className="pl-9 pr-12 h-9 bg-muted/50 border-transparent focus-visible:bg-background"
            />
            <kbd className="hidden sm:inline-flex absolute right-2 top-1/2 -translate-y-1/2 h-5 items-center gap-0.5 rounded border border-border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
              ⌘K
            </kbd>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg"
            onClick={toggle}
            aria-label="Cambiar tema"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          <NotificationsBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 px-1.5 rounded-lg gap-2">
                <div className="h-7 w-7 rounded-full bg-gradient-brand text-white text-xs font-semibold flex items-center justify-center">
                  {initials}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium">Mi cuenta</span>
                  <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/configuraciones")}>
                <Settings className="h-4 w-4 mr-2" />
                Configuración
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
